import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Send, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CALENDLY_URL } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", company: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* glow background */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(0,212,255,0.15),transparent_55%)]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-[#9be8ff]">
              <Zap className="h-3.5 w-3.5 text-[#00d4ff]" />
              Free 15-Minute Assessment
            </div>
            <h2
              data-testid="contact-headline"
              className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]"
            >
              Ready to Electrify <br />
              <span className="text-[#00d4ff]">Your Business?</span>
            </h2>
            <p className="mt-5 text-zinc-400 text-base md:text-lg max-w-md">
              Book a free 15‑minute AI assessment. We&apos;ll show you exactly where
              AI can save you time and make you money.
            </p>

            <Button
              asChild
              data-testid="contact-cta-book"
              className="mt-7 h-12 px-6 rounded-full bg-[#00d4ff] text-black hover:bg-[#33ddff] font-semibold text-sm shadow-[0_0_20px_rgba(0,212,255,0.45)] hover:shadow-[0_0_32px_rgba(0,212,255,0.65)]"
            >
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                Book Your Free Assessment
                <Calendar className="ml-2 h-4 w-4" />
              </a>
            </Button>

            <div className="mt-10 space-y-3 text-sm text-zinc-400">
              <p>
                <span className="text-zinc-200">Fort Lauderdale, FL</span> —
                serving brands worldwide.
              </p>
              <p>Reply within 24 hours, Monday–Friday.</p>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-2xl border border-white/10 bg-[#13131a]/80 p-6 md:p-8 backdrop-blur-sm"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <Field label="Full Name" htmlFor="name">
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Jane Doe"
                  autoComplete="name"
                  data-testid="contact-input-name"
                  className="h-12 bg-[#0e0e15] border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-[#00d4ff]/50 focus-visible:border-[#00d4ff]/40"
                />
              </Field>
              <Field label="Email Address" htmlFor="email">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="jane@company.com"
                  autoComplete="email"
                  data-testid="contact-input-email"
                  className="h-12 bg-[#0e0e15] border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-[#00d4ff]/50 focus-visible:border-[#00d4ff]/40"
                />
              </Field>
              <Field label="Company" htmlFor="company">
                <Input
                  id="company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Acme Inc."
                  autoComplete="organization"
                  data-testid="contact-input-company"
                  className="h-12 bg-[#0e0e15] border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-[#00d4ff]/50 focus-visible:border-[#00d4ff]/40"
                />
              </Field>
              <Field label="Phone (optional)" htmlFor="phone">
                <Input
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+1 555 123 4567"
                  autoComplete="tel"
                  data-testid="contact-input-phone"
                  className="h-12 bg-[#0e0e15] border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-[#00d4ff]/50 focus-visible:border-[#00d4ff]/40"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="How can we help your business?" htmlFor="message">
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell us about your goals, challenges, or what you'd like to automate…"
                    rows={5}
                    data-testid="contact-input-message"
                    className="bg-[#0e0e15] border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-[#00d4ff]/50 focus-visible:border-[#00d4ff]/40"
                  />
                </Field>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-form-submit"
              className="mt-6 h-12 w-full rounded-full bg-[#00d4ff] text-black hover:bg-[#33ddff] font-semibold shadow-[0_0_20px_rgba(0,212,255,0.4)] hover:shadow-[0_0_32px_rgba(0,212,255,0.6)] transition-all"
            >
              {submitting ? "Sending…" : "Send Message"}
              {!submitting && <Send className="ml-2 h-4 w-4" />}
            </Button>

            <p className="mt-3 text-center text-xs text-zinc-500">
              We respect your inbox. No spam. No newsletters. Just clear,
              direct replies.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
